/**
 * Digilync API Client
 * Handles communication between frontend, backend API, and file storage URLs.
 * Uses correct backend based on where the frontend is hosted (runtime detection).
 */

function getApiBaseUrl() {
  if (process.env.REACT_APP_API_URL) {
    return String(process.env.REACT_APP_API_URL).replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return ''; // CRA dev proxy
    if (/digilync\.net$/i.test(host)) return 'https://api.digilync.net';
  }
  if (process.env.NODE_ENV !== 'production') return '';
  return 'https://api.digilync.net';
}

function resolveApiBaseUrl() {
  return getApiBaseUrl();
}

/**
 * Base fetch wrapper with error handling and JSON parsing
 * @param {string} endpoint - API endpoint (e.g. '/api/health')
 * @param {RequestInit} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<{ data?: any, error?: string }>}
 */
const API_TIMEOUT_MS = 6000;

function getAdminHeaders() {
  try {
    const stored = localStorage.getItem('digilync_admin');
    if (!stored) return {};
    const admin = JSON.parse(stored);
    return admin?.id != null
      ? { 'X-Admin-Id': String(admin.id), 'X-Admin-Username': admin.username || '' }
      : {};
  } catch {
    return {};
  }
}

async function apiRequest(endpoint, options = {}) {
  const url = `${resolveApiBaseUrl()}${endpoint}`;
  const timeoutMs = options.timeoutMs ?? API_TIMEOUT_MS;
  const { timeoutMs: _omit, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const adminHeaders = endpoint.startsWith('/api/auth/login') ? {} : getAdminHeaders();
  const config = {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...adminHeaders,
      ...fetchOptions.headers,
    },
    signal: controller.signal,
  };

  try {
    const response = await fetch(url, config);
    clearTimeout(timeoutId);
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        error: data?.message || data?.error || `Request failed (${response.status})`,
        status: response.status,
      };
    }

    return { data, status: response.status };
  } catch (err) {
    clearTimeout(timeoutId);
    const raw = String(err.message || '');
    const looksLikeFetchFail =
      err.name === 'TypeError' || /failed to fetch|networkerror|load failed/i.test(raw);
    const msg =
      err.name === 'AbortError'
        ? 'Request timed out. Check your connection and try again.'
        : looksLikeFetchFail
          ? 'Could not reach the Digilync API. If this persists, the server may need a CORS or network fix.'
          : 'Something went wrong';
    return {
      error: msg,
      status: null,
    };
  }
}

/**
 * API methods for common operations
 */
export const api = {
  /** Health check */
  health: () => apiRequest('/api/health'),

  /** Public metrics (for landing page live stats) */
  getPublicMetrics: () => apiRequest('/api/public/metrics'),

  /** WhatsApp farmer registration — save GPS from web capture page */
  submitFarmerRegisterGps: (body) =>
    apiRequest('/api/public/farmer-register-gps', {
      method: 'POST',
      body: JSON.stringify(body),
      timeoutMs: 20000,
    }),

  /** WhatsApp provider registration — save base location from same /gps page with role=provider */
  submitProviderRegisterGps: (body) =>
    apiRequest('/api/public/provider-register-gps', {
      method: 'POST',
      body: JSON.stringify(body),
      timeoutMs: 20000,
    }),

  /** WhatsApp service request — confirm job location (?purpose=request on /gps) */
  submitServiceRequestGps: (body) =>
    apiRequest('/api/public/service-request-gps', {
      method: 'POST',
      body: JSON.stringify(body),
      timeoutMs: 20000,
    }),

  /** Public map locations (farmers, plots, providers with GPS for landing page map) */
  getPublicLocations: () => apiRequest('/api/public/locations'),

  /** Admin auth */
  login: (username, password) =>
    apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  /** Dashboard */
  getDashboardStats: () => apiRequest('/api/dashboard/stats'),

  /** Analytics */
  getAnalyticsOverview: () => apiRequest('/api/analytics/overview'),
  getAnalyticsServices: () => apiRequest('/api/analytics/services'),
  getAnalyticsCrops: () => apiRequest('/api/analytics/crops'),
  getAnalyticsBookingsTrend: () => apiRequest('/api/analytics/bookings-trend'),

  /** Ratings (farmer ratings of providers) */
  getRatingsSummary: () => apiRequest('/api/ratings/summary'),
  getRatingsRecent: () => apiRequest('/api/ratings/recent'),

  /** Notifications */
  getNotifications: () => apiRequest('/api/notifications'),

  /** Farmers */
  getFarmersMapData: () => apiRequest('/api/farmers/map-data'),
  getFarmers: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return apiRequest(`/api/farmers${q ? `?${q}` : ''}`);
  },
  getFarmer: (id) => apiRequest(`/api/farmers/${id}`),
  createFarmer: (data) =>
    apiRequest('/api/farmers', { method: 'POST', body: JSON.stringify(data) }),
  updateFarmer: (id, data) =>
    apiRequest(`/api/farmers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFarmer: (id) =>
    apiRequest(`/api/farmers/${id}`, { method: 'DELETE' }),

  /** Providers */
  getProvidersMapData: () => apiRequest('/api/providers/map-data'),
  getProviders: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return apiRequest(`/api/providers${q ? `?${q}` : ''}`);
  },
  getProvider: (id) => apiRequest(`/api/providers/${id}`),
  createProvider: (data) =>
    apiRequest('/api/providers', { method: 'POST', body: JSON.stringify(data) }),
  updateProvider: (id, data) =>
    apiRequest(`/api/providers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  patchProvider: (id, data) =>
    apiRequest(`/api/providers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteProvider: (id) =>
    apiRequest(`/api/providers/${id}`, { method: 'DELETE' }),

  /** Bookings */
  getBookings: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return apiRequest(`/api/bookings${q ? `?${q}` : ''}`);
  },
  getBooking: (id) => apiRequest(`/api/bookings/${id}`),
  createBooking: (data) =>
    apiRequest('/api/bookings', { method: 'POST', body: JSON.stringify(data) }),
  updateBooking: (id, data) =>
    apiRequest(`/api/bookings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBooking: (id) =>
    apiRequest(`/api/bookings/${id}`, { method: 'DELETE' }),

  /** Provider availability slots */
  getProviderAvailability: (providerId, params) => {
    const q = new URLSearchParams(params || {}).toString();
    return apiRequest(`/api/availability/provider/${providerId}${q ? `?${q}` : ''}`);
  },
  createProviderAvailability: (providerId, data) =>
    apiRequest(`/api/availability/provider/${providerId}`, { method: 'POST', body: JSON.stringify(data) }),
  deleteProviderAvailability: (id) =>
    apiRequest(`/api/availability/${id}`, { method: 'DELETE' }),
  updateProviderAvailability: (id, data) =>
    apiRequest(`/api/availability/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  /** Job execution events */
  getBookingJobEvents: (bookingId) =>
    apiRequest(`/api/job-events/booking/${bookingId}`),
  createBookingJobEvent: (bookingId, data) =>
    apiRequest(`/api/job-events/booking/${bookingId}`, { method: 'POST', body: JSON.stringify(data) }),

  /** Booking disputes */
  getDisputes: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return apiRequest(`/api/disputes${q ? `?${q}` : ''}`);
  },
  createDispute: (data) =>
    apiRequest('/api/disputes', { method: 'POST', body: JSON.stringify(data) }),
  resolveDispute: (id, data) =>
    apiRequest(`/api/disputes/${id}/resolve`, { method: 'PUT', body: JSON.stringify(data) }),

  /** Admin ratings (rate farmers and providers) */
  getAdminRatings: (rateeType, rateeId) =>
    apiRequest(`/api/admin-ratings?ratee_type=${rateeType}&ratee_id=${rateeId}`),
  submitAdminRating: (data) =>
    apiRequest('/api/admin-ratings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Recommendations & manual matching */
  getRecommendations: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return apiRequest(`/api/recommendations?${q}`);
  },
  matchBooking: (bookingId, providerId) =>
    apiRequest(`/api/bookings/${bookingId}/match`, {
      method: 'POST',
      body: JSON.stringify({ provider_id: providerId }),
    }),

  /** Booking portals (farmer/provider) - JSON endpoints for frontend portals */
  getPortalInfo: (token) => apiRequest(`/api/booking-portals/json/info?t=${encodeURIComponent(token)}`),
  submitFarmerConfirmation: (token, decision) =>
    apiRequest('/api/booking-portals/json/farmer/submit', { method: 'POST', body: JSON.stringify({ token, decision }) }),
  submitProviderPayout: (token, method, number) =>
    apiRequest('/api/booking-portals/json/provider/submit', { method: 'POST', body: JSON.stringify({ token, method, number }) }),

  /** Farm plots (multiple GPS per farmer) */
  getFarmPlots: (farmerId) =>
    apiRequest(`/api/farm-plots?farmer_id=${farmerId}`),
  createFarmPlot: (data) =>
    apiRequest('/api/farm-plots', { method: 'POST', body: JSON.stringify(data) }),
  updateFarmPlot: (id, data) =>
    apiRequest(`/api/farm-plots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFarmPlot: (id) =>
    apiRequest(`/api/farm-plots/${id}`, { method: 'DELETE' }),

  /** Audit logs */
  getAuditLogs: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return apiRequest(`/api/audit-logs${q ? `?${q}` : ''}`);
  },

  /** Platform settings (admin) */
  getSettings: () => apiRequest('/api/settings'),
  updateSettings: (settings) =>
    apiRequest('/api/settings', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    }),

  /** Maintenance banner (admin + public) */
  getMaintenanceSettings: () => apiRequest('/api/settings/maintenance'),
  updateMaintenanceSettings: (maintenanceSignalEnabled) =>
    apiRequest('/api/settings/maintenance', {
      method: 'PUT',
      body: JSON.stringify({ maintenanceSignalEnabled }),
    }),
  getPublicMaintenance: () => apiRequest('/api/public/maintenance'),
};

/**
 * File storage base URL (for public file links)
 * Use this when constructing URLs to files stored on FTP
 */
export const FILE_STORAGE_BASE_URL =
  process.env.REACT_APP_FILE_STORAGE_URL ||
  'https://st60307.ispot.cc/digilyncstorage';

/**
 * Build a public URL for a stored file
 * @param {string} path - Relative path from FTP base (e.g. 'uploads/image.jpg')
 * @returns {string} Full public URL
 */
export function getFileUrl(path) {
  const base = FILE_STORAGE_BASE_URL.replace(/\/$/, '');
  const cleanPath = path ? path.replace(/^\//, '') : '';
  return cleanPath ? `${base}/${cleanPath}` : base;
}

export default api;
